import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { compose } from 'recompose';
import styled from 'styled-components';
import { hot } from 'react-hot-loader';
import { Link } from 'react-router-dom';

import { Card, Table, Input, Icon, Header, Dimmer, Loader, Breadcrumb, Container, Button, Label, Popup, Divider } from 'semantic-ui-react';

import { withLocale } from 'op/components/locale';
import { Evaluation } from 'op/components/evaluation';
import { withFormItemReports } from './formitem-reports.helper';

import axios from "axios";
import { ActionBar } from '../../person/search/person-search.container';

const formatDateTime = (dateTimeStr) => {
  const formatter = new Intl.DateTimeFormat('default', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });

  const formatted = formatter.format(new Date(dateTimeStr));

  return formatted;
}

const renderFormItemRecord = (formItemRecord, locale) => (
  <Table.Row key={formItemRecord.itemReportFormID}>
     <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.itemReportName}:
      </Header>
      {formItemRecord.itemReportName}
    </Table.Cell>
    <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'} >
        {locale.dateTime}:
      </Header>
      {formatDateTime(formItemRecord.itemReportFormDate)}
    </Table.Cell>
    <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.checkedBy}:
      </Header>
      {formItemRecord.checkedBy}
    </Table.Cell>
    <Table.Cell>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.name}:
      </Header>
      {formItemRecord.genName}
    </Table.Cell>
    <Table.Cell>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.comments}:
      </Header>
      {formItemRecord.itemReportFormRemarks}
    </Table.Cell>
    <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.status}:
      </Header>
      <Label color={formItemRecord.itemReportOverallStatus ? "green" : "red"}>{
        formItemRecord.itemReportOverallStatus
          ? locale.pass
          : locale.fail
      }</Label>
    </Table.Cell>
  </Table.Row>
);

const EmptyPage = styled.div`
  display: flex;

  height: 100%;
  width: 100%;

  align-items: center;
  justify-content: center;

  padding-top: 2rem;
  padding-bottom: 10rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > h2 {
    margin: 0 !important;
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableHeaderOnDesktop = styled(Table.Header)`
  @media (max-width: 767px) {
    display: none !important;
  }
`;

const FormItemReports = ({ locale, formItemReports, item, loading, fetchFormItemReports }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchFormItemReports({ page });
  }, [page]);

  const noMoreRecords = useMemo(() => {
    if (loading) return false;

    if (!formItemReports || formItemReports.length == 0) return true;

    if (formItemReports.length % 10 != 0) return true;

    if ((page + 1) * 10 > formItemReports.length) return true;

    return false;
  }, [loading, formItemReports, page]);

  return (
    <Card.Group>
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Breadcrumb>
              <Breadcrumb.Section link as={Link} to="/stp">{locale.search}</Breadcrumb.Section>
              <Breadcrumb.Divider />
              <Breadcrumb.Section active>{item.ASM_Item_Name}</Breadcrumb.Section>
            </Breadcrumb>
            <Loader active={loading == 'partial'} inline size='tiny' style={{ float: locale._meta.rtl ? 'left' : 'right' }} />
          </Card.Header>
          {
            formItemReports && formItemReports.length > 0 &&
            <div>
              <Table basic='very' celled>
                <Table.Body>
                  {formItemReports.map(formItemRecord => renderFormItemRecord(formItemRecord, locale))}
                </Table.Body>
              </Table>
              <LoadMoreContainer>
                <Button
                  color='blue'
                  onClick={() => setPage(page + 1)}
                  loading={loading}
                  disabled={loading || noMoreRecords}
                  content="More" />
              </LoadMoreContainer>
            </div>
          }
          {
            !(formItemReports && formItemReports.length > 0) &&
            <EmptyPage>
              <Header as='h2' icon>
                <Icon name='space shuttle' />
                {locale.empty.header}
                <Header.Subheader>{locale.empty.content}</Header.Subheader>
              </Header>
            </EmptyPage>
          }
        </Card.Content>
        <Dimmer active={loading == 'full'}><Loader content='Loading' /></Dimmer>
      </Card>
      <ActionBar direction={locale._meta.rtl ? 'left' : 'right'}>
        <Button icon={'plus'} disabled={false} circular
          size='huge' primary
          as={Link} to={`/forms/${item.ASM_Item_ID}/records/new`}
        />
      </ActionBar>
    </Card.Group>
  );
}

export default compose(
  hot(module),
  withLocale('forms.formItemReports'),
  withFormItemReports
)(FormItemReports);
